import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    ViewEncapsulation,
    Inject, HostListener, inject,
} from '@angular/core';
import {mixinDisabled} from '@angular/material/core';
import {merge, Subject, Subscription} from 'rxjs';
import {CustomSortDirection, CustomSort, CustomSortable} from './custom.sort';
import {customSortAnimations} from './custom.sort.animations';
import {TableEventService} from '@app/modules/table/services/table-event.service';


// Класс для нанесения значений на заголовок сортировки.
export class CustomSortHeaderBase {
}

export const _CustomSortHeaderMixinBase: any =
    mixinDisabled(CustomSortHeaderBase);

/**
 * Допустимые позиции для стрелки для ее непрозрачности и положения.
 */
export type ArrowViewState = CustomSortDirection | 'hint' | 'active';

/**
 * Состояния, описывающие анимированную позицию стрелки (анимация от состояния до состояния).
 * Если значение fromState не определено, анимированный переход к toState не будет выполнен.
 */
export interface ArrowViewStateTransition {
    fromState?: ArrowViewState;
    toState: ArrowViewState;
}

/** Определение столбца, связанного с `CustomSortHeader`. */
interface CustomSortHeaderColumnDef {
    name: string;
}

/**
 * Применяет поведение сортировки (нажмите, чтобы изменить сортировку) и стили к элементу, включая
 * стрелку для отображения текущего направления сортировки.
 *
 * Должен иметь идентификатор и содержаться в родительской директиве CustomSort.
 *
 * Если используется в ячейках заголовка в CdkTable, он автоматически по умолчанию будет использовать свой идентификатор из содержащего
 * определение столбца.
 */
@Component({
    selector: '[custom-sort-header]',
    exportAs: 'customSortHeader',
    templateUrl: 'custom.sort.header.html',
    styleUrls: ['custom.sort.header.scss'],
    host: {
        '(click)': '_handleClick(null)',
        '(mouseenter)': '_setIndicatorHintVisible(true)',
        '(longpress)': '_setIndicatorHintVisible(true)',
        '(mouseleave)': '_setIndicatorHintVisible(false)',
        '[attr.aria-sort]': '_getAriaSortAttribute()',
        '[class.custom-sort-header-disabled]': '_isDisabled()',
    },
    changeDetection: ChangeDetectionStrategy.OnPush,
    inputs: ['disabled'],
    animations: [
        customSortAnimations.indicator,
        customSortAnimations.leftPointer,
        customSortAnimations.rightPointer,
        customSortAnimations.arrowOpacity,
        customSortAnimations.arrowPosition,
        customSortAnimations.allowChildren,
    ]
})
export class CustomSortHeader extends _CustomSortHeaderMixinBase
    implements CustomSortable, OnDestroy, OnInit {
    private _rerenderSubscription: Subscription;

    /**
     * Флаг установлен в значение true, когда индикатор должен отображаться, когда сортировка не активна. Используется чтобы
     * Обеспечить возможность сортировки заголовка, показывая фокусировку и зависание.
     */
    _showIndicatorHint: boolean = false;

    /**
     * Вид перехода состояния стрелки (перевод / непрозрачность) - указывает ее `от` и` до`
     * позиция через анимацию. Если анимации в настоящее время отключены, fromState удаляется
     * чтобы анимация не отображалась.
     */
    _viewState: ArrowViewStateTransition;

    /** Направление стрелки должно быть в соответствии с текущим состоянием. */
    _arrowDirection: CustomSortDirection = '';

    /**
     * Должна ли анимация состояния просмотра показывать переход между состояниями `from` и` to`.
     */
    _disableViewStateAnimation = false;

    /**
     * Событие изменения состояния. Используйте это, чтобы уведомить
     * компоненты, если метки изменились после инициализации.
     */
    readonly changes: Subject<void> = new Subject<void>();

    /**
     * Идентификатор этого вида заголовка. Если используется в контексте CdkColumnDef, по умолчанию это
     * название столбца.
     */
    @Input('custom-sort-header') id: string;

    /** Устанавливает положение стрелки, которая отображается при сортировке. */
    @Input() arrowPosition: 'before' | 'after' = 'after';

    /** Переопределяет начальное значение сортировки содержащей CustomSort для этого CustomSortable. */
    @Input() start: 'asc' | 'desc';

    /** Переопределяет отключить очистить значение содержащей CustomSort для этой CustomSortable. */
    @Input()
    get disableClear(): boolean {
        return this._disableClear;
    }

    set disableClear(v) {
        this._disableClear = coerceBooleanProperty(v);
    }

    private _disableClear: boolean;

    /**
     * Сервис событий в таблице
     */
    tableEventService: TableEventService = inject(TableEventService);

    constructor(changeDetectorRef: ChangeDetectorRef,
                @Optional() public _sort: CustomSort,
                @Inject('CUSTOM_SORT_HEADER_COLUMN_DEF') @Optional()
                public _columnDef: CustomSortHeaderColumnDef) {
        // Обратите внимание, что мы используем строковый токен для `_columnDef`, потому что значение предоставляется как
        // `material/table` и` cdk/table`, и у нас не может быть CDK связан с material таблицей,
        // и нужно избежать заголовка сортировки связанного с таблицей CDK, потому что
        // этой единственные ссылки.
        super();

        if (!_sort) {
            throw Error(`CustomSortHeader must be placed within a parent element with the CustomSort directive.`);
        }

        this._rerenderSubscription = merge(_sort.sortChange, _sort._stateChanges, this.changes)
            .subscribe(() => {
                if (this._isSorted()) {
                    this._updateArrowDirection();
                }

                // Если этот заголовок был недавно активен и теперь не сортируется, анимируем стрелку.
                if (!this._isSorted() && this._viewState && this._viewState.toState === 'active') {
                    this._disableViewStateAnimation = false;
                    this._setAnimationTransitionState({fromState: 'active', toState: this._arrowDirection});
                }

                changeDetectorRef.markForCheck();
            });
    }

    ngOnInit() {
        if (!this.id && this._columnDef) {
            this.id = this._columnDef.name;
        }

        // Инициализируйте направление стрелки и устанавливаем состояние просмотра.
        this._updateArrowDirection();
        this._setAnimationTransitionState(
            {toState: this._isSorted() ? 'active' : this._arrowDirection});

        this._sort.register(this);
    }

    ngOnDestroy() {
        this._sort.deregister(this);
        this._rerenderSubscription?.unsubscribe();
    }

    /**
     * Устанавливает состояние «hint» таким образом, что стрелка будет полупрозрачно отображаться как подсказка для
     * пользователя, показывать, чем станет активная сортировка. Если установлено значение false, стрелка исчезнет.
     */
    _setIndicatorHintVisible(visible: boolean) {
        // Нет, если сортировка заголовка отключена - подсказка не должна быть видимой.
        if (this._isDisabled() && visible) {
            return;
        }

        this._showIndicatorHint = visible;

        if (!this._isSorted()) {
            this._updateArrowDirection();
            if (this._showIndicatorHint) {
                this._setAnimationTransitionState({fromState: this._arrowDirection, toState: 'hint'});
            } else {
                this._setAnimationTransitionState({fromState: 'hint', toState: this._arrowDirection});
            }
        }
    }

    /**
     * Устанавливает состояние просмотра перехода анимации для положения и непрозрачности стрелки. Если
     * флаг disableViewStateAnimation` установлен в true, `fromState` будет игнорироваться, так что
     * анимация не отображается.
     */
    _setAnimationTransitionState(viewState: ArrowViewStateTransition) {
        this._viewState = viewState;

        // Если анимация для состояния положения стрелки (непрозрачность / перевод) должна быть отключена,
        // удаляем fromState так, чтобы он переходил прямо к toState.
        if (this._disableViewStateAnimation) {
            this._viewState = {toState: viewState.toState};
        }
    }

    /** Запускает сортировку в этом заголовке сортировки и удаляет подсказку индикатора. */
    @HostListener('click', ['$event'])
    _handleClick($event) {
        if (this._isDisabled() || this.tableEventService.isResizing) {
            return;
        }

        this._sort.sort(this, $event.ctrlKey);

        // Не показывать анимацию, если заголовок уже был показан в правильном положении.
        if (this._viewState.toState === 'hint' || this._viewState.toState === 'active') {
            this._disableViewStateAnimation = true;
        }

        // Если стрелка теперь отсортирована, анимируйте стрелку на место. В противном случае, анимируем ее в
        // направление, на которое оно смотрит.
        const viewState: ArrowViewStateTransition = this._isSorted() ?
            {fromState: this._arrowDirection, toState: 'active'} :
            {fromState: 'active', toState: this._arrowDirection};
        this._setAnimationTransitionState(viewState);

        this._showIndicatorHint = false;
    }

    /** Будет ли данный CustomSortHeader отсортирован в порядке возрастания или убывания. */
    _isSorted() {
        let is_active = false;
        this._sort.active.forEach(active => {
            if (active.name == this.id) {
                is_active = true;
            }
        });
        return is_active &&
            (this._sort.getDirection(this.id) === 'asc' || this._sort.getDirection(this.id) === 'desc');
    }

    /** Возвращает состояние анимации для направления стрелки (указатель и указатели). */
    _getArrowDirectionState() {
        return `${this._isSorted() ? 'active-' : ''}${this._arrowDirection}`;
    }

    /** Возвращает состояние положения стрелки (непрозрачность, трансформация). */
    _getArrowViewState() {
        const fromState = this._viewState.fromState;
        return (fromState ? `${fromState}-to-` : '') + this._viewState.toState;
    }

    /**
     * Обновляет направление, на которое должна указывать стрелка.
     */
    _updateArrowDirection() {
        this._arrowDirection = this._isSorted() ?
            this._sort.getDirection(this.id) :
            (this.start || this._sort.start);
    }

    _isDisabled() {
        return this._sort.disabled || this.disabled;
    }

    /**
     * Получает атрибут aria-sort, который следует применить к этому заголовку сортировки. Если этот заголовок
     * не сортируется, возвращает ноль, поэтому атрибут удаляется из элемента хоста. Aria spec
     * говорит, что свойство aria-sort должно присутствовать только в одном заголовке за раз, поэтому удаление
     * гарантирует, что это правда.
     */
    _getAriaSortAttribute() {
        if (!this._isSorted()) {
            return null;
        }

        return this._sort.getDirection(this.id) == 'asc' ? 'ascending' : 'descending';
    }

    /** Ярлык для кнопки сортировки. */
    sortButtonLabel = (id: string) => {
        return `Change sorting for ${id}`;
    };
}
